import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';

type MonitorSettings = {
  monitoringEnabled: boolean;
  baseIntervalMinutes: number;
  lowSpaceThresholdGb: number;
  recoveryThresholdGb: number;
  chartMinGb: number;
  chartMaxGb: number;
  chartGridStepGb: number;
  monthlyChartMinGb: number;
  monthlyChartMaxGb: number;
  monthlyChartGridStepGb: number;
  monthlySftpChartMinGb: number;
  monthlySftpChartMaxGb: number;
  monthlySftpChartGridStepGb: number;
  sftpMonitoringEnabled: boolean;
  sftpBaseIntervalMinutes: number;
  sftpStaleMultiplier: number;
  sftpLowSpaceThresholdGb: number;
  sftpRecoveryThresholdGb: number;
  sftpReminderIntervalMinutes: number;
  sftpRetentionDays: number;
  reminderIntervalMinutes: number;
  dailySummaryEnabled: boolean;
  dailySummaryTime: string;
  dailySummaryTimezone: string;
  retentionDays: number;
  pageSizeMeasurements: number;
  pageSizeNotifications: number;
  staleMultiplier: number;
  monitorPath: string;
};

type SchedulerState = {
  schedulerStatus: string;
  schedulerInstanceId?: string | null;
  lastSchedulerHeartbeatAt?: string | null;
  lastCheckStatus?: string | null;
  lastCheckAt?: string | null;
  lastSuccessfulCheckAt?: string | null;
  lastErrorMessage?: string | null;
};

type StatusResponse = {
  monitorPath: string;
  settings: MonitorSettings;
  schedulerState: SchedulerState;
  latestMeasurement?: any;
  latestSftpMeasurement?: any;
  activeIncident?: any;
  sftpState?: {
    enabled: boolean;
    path: string;
    schedulerStatus: string;
    lastCheckAt?: string | null;
    lastSuccessfulCheckAt?: string | null;
  };
};

type DailyChartPoint = {
  id: string;
  checkedAt: string;
  freeGb: number | null;
};

type MonthlyChartPoint = {
  id: string;
  date: string;
  sampledAt?: string;
  checkedAt: string;
  freeGb: number | null;
};

type MonthlyChartResponse = {
  source: 'prod' | 'sftp';
  from: string;
  to: string;
  points: MonthlyChartPoint[];
};

const MONTHLY_CHART_SOURCE_STORAGE_KEY = 'serverSettings.monthlyChartSource';
type CollapsibleSectionKey = 'monitoringSettings' | 'measurements' | 'notifications' | 'incidents';

const initialSettings: MonitorSettings = {
  monitoringEnabled: true,
  baseIntervalMinutes: 5,
  lowSpaceThresholdGb: 10,
  recoveryThresholdGb: 11,
  chartMinGb: 0,
  chartMaxGb: 100,
  chartGridStepGb: 10,
  monthlyChartMinGb: 0,
  monthlyChartMaxGb: 100,
  monthlyChartGridStepGb: 10,
  monthlySftpChartMinGb: 0,
  monthlySftpChartMaxGb: 100,
  monthlySftpChartGridStepGb: 10,
  sftpMonitoringEnabled: false,
  sftpBaseIntervalMinutes: 15,
  sftpStaleMultiplier: 3,
  sftpLowSpaceThresholdGb: 10,
  sftpRecoveryThresholdGb: 11,
  sftpReminderIntervalMinutes: 10,
  sftpRetentionDays: 90,
  reminderIntervalMinutes: 10,
  dailySummaryEnabled: true,
  dailySummaryTime: '09:00',
  dailySummaryTimezone: 'Europe/Chisinau',
  retentionDays: 90,
  pageSizeMeasurements: 50,
  pageSizeNotifications: 50,
  staleMultiplier: 3,
  monitorPath: '/'
};

const ServerSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checkingNow, setCheckingNow] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [form, setForm] = useState<MonitorSettings>(initialSettings);

  const [measurements, setMeasurements] = useState<any[]>([]);
  const [measurementsPage, setMeasurementsPage] = useState(1);
  const [measurementsTotalPages, setMeasurementsTotalPages] = useState(1);

  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsTotalPages, setNotificationsTotalPages] = useState(1);

  const [incidents, setIncidents] = useState<any[]>([]);
  const [incidentsPage, setIncidentsPage] = useState(1);
  const [incidentsTotalPages, setIncidentsTotalPages] = useState(1);
  const [incidentsPageSize, setIncidentsPageSize] = useState(10);
  const [dailyChartPoints, setDailyChartPoints] = useState<DailyChartPoint[]>([]);
  const [monthlyChartProdPoints, setMonthlyChartProdPoints] = useState<MonthlyChartPoint[]>([]);
  const [monthlyChartSftpPoints, setMonthlyChartSftpPoints] = useState<MonthlyChartPoint[]>([]);
  const [monthlyChartSource, setMonthlyChartSource] = useState<'prod' | 'sftp'>(() => {
    if (typeof window === 'undefined') return 'prod';
    const saved = String(window.localStorage.getItem(MONTHLY_CHART_SOURCE_STORAGE_KEY) || '').trim().toLowerCase();
    return saved === 'sftp' ? 'sftp' : 'prod';
  });
  const [monthlyChartFrom, setMonthlyChartFrom] = useState<string>('');
  const [monthlyChartTo, setMonthlyChartTo] = useState<string>('');
  const [dailyChartBucketMinutes, setDailyChartBucketMinutes] = useState<number>(5);
  const [dailyChartViewMode, setDailyChartViewMode] = useState<'fact' | 'aggregation' | 'hybrid'>('aggregation');
  const [collapsedSections, setCollapsedSections] = useState<Record<CollapsibleSectionKey, boolean>>({
    monitoringSettings: true,
    measurements: true,
    notifications: true,
    incidents: true
  });

  const toggleSection = (section: CollapsibleSectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const loadStatus = useCallback(async () => {
    const data = await apiGet<StatusResponse>('/server-monitor/status');
    setStatus(data);
    setForm({
      ...initialSettings,
      ...data.settings,
      monitorPath: data.monitorPath || data.settings.monitorPath || '/'
    });
  }, []);

  const loadMeasurements = useCallback(async (page: number, limit: number) => {
    const data = await apiGet<{ items: any[]; pagination: { totalPages: number } }>('/server-monitor/measurements', { page, limit });
    setMeasurements(data.items || []);
    setMeasurementsTotalPages(data.pagination?.totalPages || 1);
  }, []);

  const loadNotifications = useCallback(async (page: number, limit: number) => {
    const data = await apiGet<{ items: any[]; pagination: { totalPages: number } }>('/server-monitor/notifications', { page, limit });
    setNotifications(data.items || []);
    setNotificationsTotalPages(data.pagination?.totalPages || 1);
  }, []);

  const loadIncidents = useCallback(async (page: number, limit: number) => {
    const data = await apiGet<{ items: any[]; pagination?: { totalPages?: number } }>('/server-monitor/incidents', { page, limit });
    setIncidents(data.items || []);
    setIncidentsTotalPages(data.pagination?.totalPages || 1);
  }, []);

  const loadDailyChart = useCallback(async () => {
    const data = await apiGet<{ points: DailyChartPoint[] }>('/server-monitor/daily-chart');
    setDailyChartPoints(Array.isArray(data?.points) ? data.points : []);
  }, []);

  const loadMonthlyChart = useCallback(async () => {
    const [prodData, sftpData] = await Promise.all([
      apiGet<MonthlyChartResponse>('/server-monitor/monthly-chart', { source: 'prod' }),
      apiGet<MonthlyChartResponse>('/server-monitor/monthly-chart', { source: 'sftp' })
    ]);
    setMonthlyChartProdPoints(Array.isArray(prodData?.points) ? prodData.points : []);
    setMonthlyChartSftpPoints(Array.isArray(sftpData?.points) ? sftpData.points : []);
    setMonthlyChartFrom(String(prodData?.from || ''));
    setMonthlyChartTo(String(prodData?.to || ''));
  }, []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      await loadStatus();
      await Promise.all([
        loadMeasurements(1, 50),
        loadNotifications(1, 50),
        loadIncidents(1, incidentsPageSize),
        loadDailyChart(),
        loadMonthlyChart()
      ]);
      setMeasurementsPage(1);
      setNotificationsPage(1);
      setIncidentsPage(1);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить страницу мониторинга');
    } finally {
      setLoading(false);
    }
  }, [incidentsPageSize, loadDailyChart, loadIncidents, loadMeasurements, loadMonthlyChart, loadNotifications, loadStatus]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!loading) {
      loadMeasurements(measurementsPage, form.pageSizeMeasurements || 50).catch(() => undefined);
    }
  }, [measurementsPage, form.pageSizeMeasurements, loadMeasurements, loading]);

  useEffect(() => {
    if (!loading) {
      loadNotifications(notificationsPage, form.pageSizeNotifications || 50).catch(() => undefined);
    }
  }, [notificationsPage, form.pageSizeNotifications, loadNotifications, loading]);

  useEffect(() => {
    if (!loading) {
      loadIncidents(incidentsPage, incidentsPageSize).catch(() => undefined);
    }
  }, [incidentsPage, incidentsPageSize, loadIncidents, loading]);

  const persistPageSizeSettings = useCallback(async (nextMeasurements: number, nextNotifications: number) => {
    try {
      await apiPut('/server-monitor/settings', {
        pageSizeMeasurements: nextMeasurements,
        pageSizeNotifications: nextNotifications
      });
      setError('');
      setSuccess('Размер страницы сохранен');
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить размер страницы');
    }
  }, []);

  const handleMeasurementsPageSizeChange = (nextSize: number) => {
    const safeSize = Math.max(1, Number(nextSize) || 50);
    const notificationsSize = Math.max(1, Number(form.pageSizeNotifications) || 50);
    setForm((p) => ({ ...p, pageSizeMeasurements: safeSize }));
    setMeasurementsPage(1);
    void persistPageSizeSettings(safeSize, notificationsSize);
  };

  const handleNotificationsPageSizeChange = (nextSize: number) => {
    const safeSize = Math.max(1, Number(nextSize) || 50);
    const measurementsSize = Math.max(1, Number(form.pageSizeMeasurements) || 50);
    setForm((p) => ({ ...p, pageSizeNotifications: safeSize }));
    setNotificationsPage(1);
    void persistPageSizeSettings(measurementsSize, safeSize);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(MONTHLY_CHART_SOURCE_STORAGE_KEY, monthlyChartSource);
  }, [monthlyChartSource]);

  const validate = useMemo(() => {
    if (Number(form.recoveryThresholdGb) <= Number(form.lowSpaceThresholdGb)) {
      return 'Порог восстановления должен быть строго больше low-space порога';
    }
    if (Number(form.chartMinGb) < 0) {
      return 'Минимум графика должен быть >= 0';
    }
    if (Number(form.chartMaxGb) <= Number(form.chartMinGb)) {
      return 'Максимум графика должен быть строго больше минимума';
    }
    if (Number(form.chartGridStepGb) <= 0) {
      return 'Шаг горизонтальных линий должен быть > 0';
    }
    if (Number(form.monthlyChartMinGb) < 0) {
      return 'Минимум месячного графика должен быть >= 0';
    }
    if (Number(form.monthlyChartMaxGb) <= Number(form.monthlyChartMinGb)) {
      return 'Максимум месячного графика должен быть строго больше минимума';
    }
    if (Number(form.monthlyChartGridStepGb) <= 0) {
      return 'Шаг линий месячного графика должен быть > 0';
    }
    if (Number(form.monthlySftpChartMinGb) < 0) {
      return 'Минимум месячного SFTP графика должен быть >= 0';
    }
    if (Number(form.monthlySftpChartMaxGb) <= Number(form.monthlySftpChartMinGb)) {
      return 'Максимум месячного SFTP графика должен быть строго больше минимума';
    }
    if (Number(form.monthlySftpChartGridStepGb) <= 0) {
      return 'Шаг линий месячного SFTP графика должен быть > 0';
    }
    if (Number(form.sftpBaseIntervalMinutes) <= 0) {
      return 'Интервал SFTP должен быть > 0';
    }
    if (Number(form.sftpStaleMultiplier) <= 0) {
      return 'Stale multiplier SFTP должен быть > 0';
    }
    if (Number(form.sftpRecoveryThresholdGb) <= Number(form.sftpLowSpaceThresholdGb)) {
      return 'Порог восстановления SFTP должен быть строго больше low-space порога SFTP';
    }
    if (Number(form.sftpRetentionDays) <= 0) {
      return 'Retention SFTP должен быть > 0';
    }
    return '';
  }, [
    form.chartGridStepGb,
    form.chartMaxGb,
    form.chartMinGb,
    form.lowSpaceThresholdGb,
    form.monthlyChartGridStepGb,
    form.monthlyChartMaxGb,
    form.monthlyChartMinGb,
    form.monthlySftpChartGridStepGb,
    form.monthlySftpChartMaxGb,
    form.monthlySftpChartMinGb,
    form.recoveryThresholdGb,
    form.sftpBaseIntervalMinutes,
    form.sftpStaleMultiplier,
    form.sftpLowSpaceThresholdGb,
    form.sftpRecoveryThresholdGb,
    form.sftpRetentionDays
  ]);

  const dailyChartV2 = useMemo(() => {
    const source = dailyChartPoints
      .filter((p) => typeof p.freeGb === 'number' && Number.isFinite(p.freeGb))
      .map((p) => ({
        ts: new Date(p.checkedAt).getTime(),
        checkedAt: p.checkedAt,
        freeGb: Number(p.freeGb)
      }))
      .filter((p) => Number.isFinite(p.ts))
      .sort((a, b) => a.ts - b.ts);

    if (!source.length) {
      return {
        hasData: false,
        avgPath: '',
        bandPath: '',
        yTicks: [] as Array<{ value: number; y: number }>,
        xTicks: [] as Array<{ x: number; label: string }>,
        hourGrid: [] as Array<{ x: number; ts: number }>,
        avgPoints: [] as Array<{ x: number; y: number; avg: number; checkedAt: string; ts: number }>,
        markerPoints: [] as Array<{ x: number; y: number; avg: number; checkedAt: string }>,
        factualHourPoints: [] as Array<{ x: number; y: number; freeGb: number; checkedAt: string }>,
        factualDisplayPoints: [] as Array<{ x: number; y: number; freeGb: number; checkedAt: string }>,
        factualPath: '',
        minFree: null as number | null,
        maxFree: null as number | null,
        bucketMinutes: 5
      };
    }

    const bucketMinutes = dailyChartBucketMinutes;
    const bucketMs = bucketMinutes * 60 * 1000;
    const bucketMap = new Map<number, { sum: number; count: number; min: number; max: number; latestTs: number }>();

    source.forEach((point) => {
      const bucketTs = Math.floor(point.ts / bucketMs) * bucketMs;
      const prev = bucketMap.get(bucketTs);
      if (!prev) {
        bucketMap.set(bucketTs, {
          sum: point.freeGb,
          count: 1,
          min: point.freeGb,
          max: point.freeGb,
          latestTs: point.ts
        });
        return;
      }
      prev.sum += point.freeGb;
      prev.count += 1;
      prev.min = Math.min(prev.min, point.freeGb);
      prev.max = Math.max(prev.max, point.freeGb);
      prev.latestTs = Math.max(prev.latestTs, point.ts);
    });

    const buckets = Array.from(bucketMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([bucketTs, value]) => ({
        ts: bucketTs,
        checkedAt: new Date(value.latestTs).toISOString(),
        avg: value.sum / value.count,
        min: value.min,
        max: value.max
      }));

    if (!buckets.length) {
      return {
        hasData: false,
        avgPath: '',
        bandPath: '',
        yTicks: [] as Array<{ value: number; y: number }>,
        xTicks: [] as Array<{ x: number; label: string }>,
        hourGrid: [] as Array<{ x: number }>,
        avgPoints: [] as Array<{ x: number; y: number; avg: number; checkedAt: string; ts: number }>,
        markerPoints: [] as Array<{ x: number; y: number; avg: number; checkedAt: string }>,
        factualHourPoints: [] as Array<{ x: number; y: number; freeGb: number; checkedAt: string }>,
        factualDisplayPoints: [] as Array<{ x: number; y: number; freeGb: number; checkedAt: string }>,
        factualPath: '',
        minFree: null as number | null,
        maxFree: null as number | null,
        bucketMinutes
      };
    }

    const minFree = Math.min(...buckets.map((p) => p.min));
    const maxFree = Math.max(...buckets.map((p) => p.max));
    const normalizedChartMin = Number.isFinite(Number(form.chartMinGb)) ? Number(form.chartMinGb) : 0;
    const normalizedChartMax = Number.isFinite(Number(form.chartMaxGb)) ? Number(form.chartMaxGb) : 100;
    const safeChartMin = Math.max(0, normalizedChartMin);
    const safeChartMax = normalizedChartMax > safeChartMin ? normalizedChartMax : safeChartMin + 1;
    const yMinRaw = safeChartMin;
    const yMaxRaw = safeChartMax;
    const yRange = Math.max(yMaxRaw - yMinRaw, 1);

    const width = 1000;
    const height = 240;
    const left = 56;
    const right = 24;
    const top = 18;
    const bottom = 36;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;

    const firstTs = buckets[0].ts;
    const lastTs = buckets[buckets.length - 1].ts;
    const xRange = Math.max(lastTs - firstTs, bucketMs);

    const mapY = (value: number) => top + (1 - (value - yMinRaw) / yRange) * plotHeight;

    const mapped = buckets.map((p) => {
      const x = left + ((p.ts - firstTs) / xRange) * plotWidth;
      return {
        x,
        y: mapY(p.avg),
        yMin: mapY(p.min),
        yMax: mapY(p.max),
        avg: p.avg,
        min: p.min,
        max: p.max,
        checkedAt: p.checkedAt,
        ts: p.ts
      };
    });

    const avgPath = mapped.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
    const upper = mapped.map((p) => `L ${p.x.toFixed(2)} ${p.yMax.toFixed(2)}`).join(' ');
    const lower = [...mapped].reverse().map((p) => `L ${p.x.toFixed(2)} ${p.yMin.toFixed(2)}`).join(' ');
    const first = mapped[0];
    const bandPath = `M ${first.x.toFixed(2)} ${first.yMax.toFixed(2)} ${upper} ${lower} Z`;

    const normalizedGridStep = Number.isFinite(Number(form.chartGridStepGb)) ? Number(form.chartGridStepGb) : 10;
    const safeGridStep = normalizedGridStep > 0 ? normalizedGridStep : 10;
    const yTickValues: number[] = [];
    for (let value = yMinRaw; value <= yMaxRaw + safeGridStep * 0.0001; value += safeGridStep) {
      yTickValues.push(Math.round(value * 1000) / 1000);
      if (yTickValues.length > 500) break;
    }
    if (yTickValues.length === 0 || yTickValues[0] !== yMinRaw) {
      yTickValues.unshift(yMinRaw);
    }
    if (Math.abs(yTickValues[yTickValues.length - 1] - yMaxRaw) > 0.0001) {
      yTickValues.push(yMaxRaw);
    }
    const yTicks = yTickValues
      .filter((value, idx, arr) => idx === 0 || Math.abs(value - arr[idx - 1]) > 0.0001)
      .map((value) => ({ value, y: mapY(value) }));
    const hourGrid: Array<{ x: number; ts: number }> = [];
    const hourMs = 60 * 60 * 1000;
    const firstHourTs = Math.ceil(firstTs / hourMs) * hourMs;
    for (let ts = firstHourTs; ts <= lastTs; ts += hourMs) {
      const x = left + ((ts - firstTs) / xRange) * plotWidth;
      hourGrid.push({ x, ts });
    }
    const xTicks = hourGrid.map((tick) => {
      const hour = new Date(tick.ts).getHours();
      return {
        x: tick.x,
        label: hour === 0 ? '24' : String(hour)
      };
    });

    // Отдельный слой фактических (не агрегированных) значений: одна точка на час
    const factualByHour = new Map<number, { ts: number; checkedAt: string; freeGb: number }>();
    source.forEach((point) => {
      const hourTs = Math.floor(point.ts / hourMs) * hourMs;
      const prev = factualByHour.get(hourTs);
      if (!prev || point.ts > prev.ts) {
        factualByHour.set(hourTs, {
          ts: point.ts,
          checkedAt: point.checkedAt,
          freeGb: point.freeGb
        });
      }
    });
    const factualHourPointsRaw = Array.from(factualByHour.entries())
      .sort(([a], [b]) => a - b)
      .map(([, point]) => ({
        x: left + ((point.ts - firstTs) / xRange) * plotWidth,
        y: mapY(point.freeGb),
        freeGb: point.freeGb,
        checkedAt: point.checkedAt
      }));
    const rightEdge = left + plotWidth;
    const edgeTolerance = 0.75;
    const factualHourPoints = factualHourPointsRaw;
    const factualLinePoints =
      factualHourPoints.length === 0
        ? factualHourPoints
        : (() => {
            const withLeft =
              factualHourPoints[0].x - left > edgeTolerance
                ? [{ ...factualHourPoints[0], x: left }, ...factualHourPoints]
                : factualHourPoints;
            const last = withLeft[withLeft.length - 1];
            return rightEdge - last.x > edgeTolerance ? [...withLeft, { ...last, x: rightEdge }] : withLeft;
          })();
    const factualDisplayPoints =
      factualHourPoints.length === 0
        ? factualHourPoints
        : (() => {
            const result = [...factualHourPoints];
            while (result.length > 0) {
              const last = result[result.length - 1];
              const lastDate = new Date(last.checkedAt);
              const isHourAligned =
                lastDate.getMinutes() === 0 && lastDate.getSeconds() === 0 && lastDate.getMilliseconds() === 0;
              const isAtRightSide = last.x >= rightEdge - edgeTolerance;
              if (!isHourAligned && isAtRightSide) {
                result.pop();
                continue;
              }
              break;
            }
            return result;
          })();
    const factualPath = factualLinePoints
      .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ');

    const minIdx = mapped.reduce((best, p, idx, arr) => (p.avg < arr[best].avg ? idx : best), 0);
    const maxIdx = mapped.reduce((best, p, idx, arr) => (p.avg > arr[best].avg ? idx : best), 0);
    const markerIndexes = Array.from(new Set([0, minIdx, maxIdx, mapped.length - 1]));
    const markerPoints = markerIndexes.map((idx) => ({
      x: mapped[idx].x,
      y: mapped[idx].y,
      avg: mapped[idx].avg,
      checkedAt: mapped[idx].checkedAt
    }));

    return {
      hasData: true,
      avgPath,
      bandPath,
      yTicks,
      xTicks,
      hourGrid,
      avgPoints: mapped.map((p) => ({ x: p.x, y: p.y, avg: p.avg, checkedAt: p.checkedAt, ts: p.ts })),
      markerPoints,
      factualHourPoints,
      factualDisplayPoints,
      factualPath,
      minFree: Math.round(minFree * 100) / 100,
      maxFree: Math.round(maxFree * 100) / 100,
      bucketMinutes
    };
  }, [dailyChartPoints, dailyChartBucketMinutes, form.chartGridStepGb, form.chartMaxGb, form.chartMinGb]);

  const monthlyChartV2 = useMemo(() => {
    const pointsSource = (monthlyChartSource === 'sftp' ? monthlyChartSftpPoints : monthlyChartProdPoints)
      .filter((p) => typeof p.freeGb === 'number' && Number.isFinite(p.freeGb))
      .map((p) => ({
        date: p.date,
        ts: new Date(String(p.sampledAt || p.checkedAt || `${p.date}T00:00:00Z`)).getTime(),
        freeGb: Number(p.freeGb),
        checkedAt: p.checkedAt
      }))
      .filter((p) => Number.isFinite(p.ts))
      .sort((a, b) => a.ts - b.ts);

    const toDateTs = (iso: string, fallback: number): number => {
      const ts = new Date(iso).getTime();
      return Number.isFinite(ts) ? ts : fallback;
    };
    const dayMs = 24 * 60 * 60 * 1000;
    const fallbackEnd = Date.now();
    const rangeToTs = toDateTs(monthlyChartTo, fallbackEnd);
    const rangeFromTs = toDateTs(monthlyChartFrom, rangeToTs - 29 * dayMs);
    const startTs = Math.min(rangeFromTs, rangeToTs);
    const endTs = Math.max(rangeFromTs, rangeToTs);
    const dayTicksTs: number[] = [];
    for (let ts = startTs; ts <= endTs; ts += dayMs) {
      dayTicksTs.push(ts);
      if (dayTicksTs.length > 60) break;
    }

    if (!pointsSource.length) {
      return {
        hasData: false,
        linePath: '',
        points: [] as Array<{ x: number; y: number; freeGb: number; checkedAt: string }>,
        yTicks: [] as Array<{ value: number; y: number }>,
        xTicks: [] as Array<{ x: number; label: string }>,
        dayGrid: [] as Array<{ x: number }>,
        minFree: null as number | null,
        maxFree: null as number | null
      };
    }

    const width = 1000;
    const height = 240;
    const left = 56;
    const right = 24;
    const top = 18;
    const bottom = 36;
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;

    const sourceChartMinRaw =
      monthlyChartSource === 'sftp' ? Number(form.monthlySftpChartMinGb) : Number(form.monthlyChartMinGb);
    const sourceChartMaxRaw =
      monthlyChartSource === 'sftp' ? Number(form.monthlySftpChartMaxGb) : Number(form.monthlyChartMaxGb);
    const sourceChartGridStepRaw =
      monthlyChartSource === 'sftp'
        ? Number(form.monthlySftpChartGridStepGb)
        : Number(form.monthlyChartGridStepGb);
    const normalizedChartMin = Number.isFinite(sourceChartMinRaw) ? sourceChartMinRaw : 0;
    const normalizedChartMax = Number.isFinite(sourceChartMaxRaw) ? sourceChartMaxRaw : 100;
    const safeChartMin = Math.max(0, normalizedChartMin);
    const safeChartMax = normalizedChartMax > safeChartMin ? normalizedChartMax : safeChartMin + 1;
    const yMinRaw = safeChartMin;
    const yMaxRaw = safeChartMax;
    const yRange = Math.max(yMaxRaw - yMinRaw, 1);
    const mapY = (value: number) => top + (1 - (value - yMinRaw) / yRange) * plotHeight;

    const firstTs = dayTicksTs.length > 0 ? dayTicksTs[0] : pointsSource[0].ts;
    const lastTs = dayTicksTs.length > 0 ? dayTicksTs[dayTicksTs.length - 1] : pointsSource[pointsSource.length - 1].ts;
    const xRange = Math.max(lastTs - firstTs, 24 * 60 * 60 * 1000);
    const mapX = (ts: number) => left + ((ts - firstTs) / xRange) * plotWidth;

    const points = pointsSource.map((p) => ({
      x: mapX(p.ts),
      y: mapY(p.freeGb),
      freeGb: p.freeGb,
      checkedAt: p.checkedAt
    }));
    const linePath = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');

    const normalizedGridStep = Number.isFinite(sourceChartGridStepRaw) ? sourceChartGridStepRaw : 10;
    const safeGridStep = normalizedGridStep > 0 ? normalizedGridStep : 10;
    const yTickValues: number[] = [];
    for (let value = yMinRaw; value <= yMaxRaw + safeGridStep * 0.0001; value += safeGridStep) {
      yTickValues.push(Math.round(value * 1000) / 1000);
      if (yTickValues.length > 500) break;
    }
    if (yTickValues.length === 0 || yTickValues[0] !== yMinRaw) {
      yTickValues.unshift(yMinRaw);
    }
    if (Math.abs(yTickValues[yTickValues.length - 1] - yMaxRaw) > 0.0001) {
      yTickValues.push(yMaxRaw);
    }
    const yTicks = yTickValues
      .filter((value, idx, arr) => idx === 0 || Math.abs(value - arr[idx - 1]) > 0.0001)
      .map((value) => ({ value, y: mapY(value) }));

    const xTicks = dayTicksTs.map((ts) => ({
      x: mapX(ts),
      label: new Date(ts).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
    }));
    const dayGrid = dayTicksTs.map((ts) => ({ x: mapX(ts) }));

    const minFree = Math.min(...pointsSource.map((p) => p.freeGb));
    const maxFree = Math.max(...pointsSource.map((p) => p.freeGb));

    return {
      hasData: true,
      linePath,
      points,
      yTicks,
      xTicks,
      dayGrid,
      minFree: Math.round(minFree * 100) / 100,
      maxFree: Math.round(maxFree * 100) / 100
    };
  }, [
    form.monthlyChartGridStepGb,
    form.monthlyChartMaxGb,
    form.monthlyChartMinGb,
    form.monthlySftpChartGridStepGb,
    form.monthlySftpChartMaxGb,
    form.monthlySftpChartMinGb,
    monthlyChartFrom,
    monthlyChartProdPoints,
    monthlyChartSftpPoints,
    monthlyChartSource,
    monthlyChartTo
  ]);

  const onSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      if (validate) {
        setError(validate);
        return;
      }
      const payload = {
        monitoringEnabled: form.monitoringEnabled,
        baseIntervalMinutes: Number(form.baseIntervalMinutes),
        lowSpaceThresholdGb: Number(form.lowSpaceThresholdGb),
        recoveryThresholdGb: Number(form.recoveryThresholdGb),
        chartMinGb: Number(form.chartMinGb),
        chartMaxGb: Number(form.chartMaxGb),
        chartGridStepGb: Number(form.chartGridStepGb),
        monthlyChartMinGb: Number(form.monthlyChartMinGb),
        monthlyChartMaxGb: Number(form.monthlyChartMaxGb),
        monthlyChartGridStepGb: Number(form.monthlyChartGridStepGb),
        monthlySftpChartMinGb: Number(form.monthlySftpChartMinGb),
        monthlySftpChartMaxGb: Number(form.monthlySftpChartMaxGb),
        monthlySftpChartGridStepGb: Number(form.monthlySftpChartGridStepGb),
        sftpMonitoringEnabled: form.sftpMonitoringEnabled,
        sftpBaseIntervalMinutes: Number(form.sftpBaseIntervalMinutes),
        sftpStaleMultiplier: Number(form.sftpStaleMultiplier),
        sftpLowSpaceThresholdGb: Number(form.sftpLowSpaceThresholdGb),
        sftpRecoveryThresholdGb: Number(form.sftpRecoveryThresholdGb),
        sftpReminderIntervalMinutes: Number(form.sftpReminderIntervalMinutes),
        sftpRetentionDays: Number(form.sftpRetentionDays),
        reminderIntervalMinutes: Number(form.reminderIntervalMinutes),
        dailySummaryEnabled: form.dailySummaryEnabled,
        dailySummaryTime: form.dailySummaryTime,
        dailySummaryTimezone: form.dailySummaryTimezone,
        retentionDays: Number(form.retentionDays),
        pageSizeMeasurements: Number(form.pageSizeMeasurements),
        pageSizeNotifications: Number(form.pageSizeNotifications),
        staleMultiplier: Number(form.staleMultiplier)
      };
      await apiPut('/server-monitor/settings', payload);
      setSuccess('Настройки сохранены');
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };

  const onCheckNow = async () => {
    try {
      setCheckingNow(true);
      setError('');
      setSuccess('');
      await apiPost('/server-monitor/check-now');
      setSuccess('Ручной замер выполнен');
      await loadAll();
    } catch (e: any) {
      setError(e?.message || 'Не удалось выполнить ручной замер');
    } finally {
      setCheckingNow(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Загрузка мониторинга сервера...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">🖥️ Сервер</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Обновить
          </button>
          <button
            onClick={onCheckNow}
            disabled={checkingNow}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {checkingNow ? 'Проверка...' : 'Проверить сейчас'}
          </button>
          <button
            onClick={onSave}
            disabled={saving || !!validate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>}
      {validate && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">{validate}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Статус scheduler</div>
          <div className="mt-1 text-xl font-semibold">{status?.schedulerState?.schedulerStatus || '—'}</div>
          <div className="mt-2 text-xs text-gray-500">Инстанс: {status?.schedulerState?.schedulerInstanceId || '—'}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Последний успешный замер</div>
          <div className="mt-1 text-base font-semibold">
            {status?.schedulerState?.lastSuccessfulCheckAt ? new Date(status.schedulerState.lastSuccessfulCheckAt).toLocaleString('ru-RU') : '—'}
          </div>
          <div className="mt-2 text-xs text-gray-500">Путь: {form.monitorPath || '/'}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">Текущий инцидент</div>
          <div className="mt-1 text-base font-semibold">{status?.activeIncident ? 'ACTIVE' : 'Нет'}</div>
          <div className="mt-2 text-xs text-gray-500">
            {status?.activeIncident ? `c ${new Date(status.activeIncident.startedAt).toLocaleString('ru-RU')}` : '—'}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Суточный график</h2>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
              {[
                { key: 'fact', label: 'факт' },
                { key: 'aggregation', label: 'агрегация' },
                { key: 'hybrid', label: 'гибрид' }
              ].map((mode) => (
                <button
                  key={mode.key}
                  type="button"
                  onClick={() => setDailyChartViewMode(mode.key as 'fact' | 'aggregation' | 'hybrid')}
                  className={`px-3 py-1 text-xs font-medium transition-colors ${
                    dailyChartViewMode === mode.key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            {dailyChartViewMode !== 'fact' && (
              <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
                {[1, 5, 15, 30, 60].map((minutes) => (
                  <button
                    key={minutes}
                    type="button"
                    onClick={() => setDailyChartBucketMinutes(minutes)}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      dailyChartBucketMinutes === minutes
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {minutes}м
                  </button>
                ))}
              </div>
            )}
            <div className="text-xs text-gray-500">
              {dailyChartViewMode === 'aggregation' &&
                `Агрегация по ${dailyChartV2.bucketMinutes} минут, зона min/max + средняя линия`}
              {dailyChartViewMode === 'fact' && 'Фактические данные: одна точка на каждый час'}
              {dailyChartViewMode === 'hybrid' &&
                `Гибрид: факт поверх агрегации (${dailyChartV2.bucketMinutes}м)`}
            </div>
          </div>
        </div>
        <div className="p-4">
          {!dailyChartV2.hasData ? (
            <div className="h-56 flex items-center justify-center text-sm text-gray-500 border border-gray-200 rounded-lg">
              Недостаточно данных для построения графика
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-gray-500">
                Шкала: <span className="font-medium text-gray-700">{Number(form.chartMinGb)} GB</span> •{' '}
                <span className="font-medium text-gray-700">{Number(form.chartMaxGb)} GB</span> • Шаг:{' '}
                <span className="font-medium text-gray-700">{Number(form.chartGridStepGb)} GB</span> •{' '}
                Min: <span className="font-medium text-gray-700">{dailyChartV2.minFree} GB</span> • Max:{' '}
                <span className="font-medium text-gray-700">{dailyChartV2.maxFree} GB</span>
              </div>
              <svg viewBox="0 0 1000 240" className="w-full h-auto border border-gray-200 rounded-lg bg-slate-50" style={{ aspectRatio: '1000 / 240' }}>
                {dailyChartV2.yTicks.map((tick, idx) => {
                  const y = tick.y;
                  return (
                    <g key={`beta-y-${idx}`}>
                      <line x1={56} y1={y} x2={976} y2={y} stroke="#dbeafe" strokeWidth="1" />
                      <text x={8} y={y + 4} fontSize="9" fill="#334155">
                        {tick.value.toFixed(0)} GB
                      </text>
                    </g>
                  );
                })}

                {dailyChartV2.xTicks.map((tick, idx) => (
                  <g key={`beta-x-${idx}`}>
                    <text x={tick.x} y={226} fontSize="9" fill="#334155" textAnchor="middle">
                      {tick.label}
                    </text>
                  </g>
                ))}

                {dailyChartV2.hourGrid.map((tick, idx) => (
                  <line key={`beta-hour-${idx}`} x1={tick.x} y1={18} x2={tick.x} y2={204} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2 3" />
                ))}

                {(dailyChartViewMode === 'aggregation' || dailyChartViewMode === 'hybrid') && (
                  <>
                    <path d={dailyChartV2.bandPath} fill="#93c5fd" opacity="0.25" />
                    <path d={dailyChartV2.avgPath} fill="none" stroke="#1d4ed8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </>
                )}

                {(dailyChartViewMode === 'fact' || dailyChartViewMode === 'hybrid') &&
                  dailyChartV2.factualPath && (
                    <path
                      d={dailyChartV2.factualPath}
                      fill="none"
                      stroke="#0f172a"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.75"
                    />
                  )}

                {(dailyChartViewMode === 'fact' || dailyChartViewMode === 'hybrid') &&
                  dailyChartV2.factualDisplayPoints.map((point, idx) => (
                    <g key={`beta-hf-${idx}`}>
                      <circle cx={point.x} cy={point.y} r={2} fill="#0f172a" opacity="0.8" />
                      <circle cx={point.x} cy={point.y} r={1} fill="#f8fafc" />
                      <title>{`${new Date(point.checkedAt).toLocaleString('ru-RU')} • факт ${point.freeGb.toFixed(2)} GB`}</title>
                    </g>
                  ))}

                {(dailyChartViewMode === 'aggregation' || dailyChartViewMode === 'hybrid') &&
                  dailyChartV2.markerPoints.map((point, idx) => (
                    <g key={`beta-m-${idx}`}>
                      <circle cx={point.x} cy={point.y} r={2} fill="#ffffff" stroke="#1e40af" strokeWidth="1" />
                      <title>{`${new Date(point.checkedAt).toLocaleString('ru-RU')} • avg ${point.avg.toFixed(2)} GB`}</title>
                    </g>
                  ))}
              </svg>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Месячный график (30 дней)</h2>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {[
              { key: 'prod', label: 'PROD' },
              { key: 'sftp', label: 'SFTP' }
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setMonthlyChartSource(tab.key as 'prod' | 'sftp')}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  monthlyChartSource === tab.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-4">
          {!monthlyChartV2.hasData ? (
            <div className="h-56 flex items-center justify-center text-sm text-gray-500 border border-gray-200 rounded-lg">
              Недостаточно данных для построения графика ({monthlyChartSource.toUpperCase()})
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-gray-500">
                Шкала: <span className="font-medium text-gray-700">{Number(monthlyChartSource === 'sftp' ? form.monthlySftpChartMinGb : form.monthlyChartMinGb)} GB</span> •{' '}
                <span className="font-medium text-gray-700">{Number(monthlyChartSource === 'sftp' ? form.monthlySftpChartMaxGb : form.monthlyChartMaxGb)} GB</span> • Шаг:{' '}
                <span className="font-medium text-gray-700">{Number(monthlyChartSource === 'sftp' ? form.monthlySftpChartGridStepGb : form.monthlyChartGridStepGb)} GB</span> • Min:{' '}
                <span className="font-medium text-gray-700">{monthlyChartV2.minFree} GB</span> • Max:{' '}
                <span className="font-medium text-gray-700">{monthlyChartV2.maxFree} GB</span>
              </div>
              <svg viewBox="0 0 1000 240" className="w-full h-auto border border-gray-200 rounded-lg bg-slate-50" style={{ aspectRatio: '1000 / 240' }}>
                {monthlyChartV2.yTicks.map((tick, idx) => (
                  <g key={`month-y-${idx}`}>
                    <line x1={56} y1={tick.y} x2={976} y2={tick.y} stroke="#dbeafe" strokeWidth="1" />
                    <text x={8} y={tick.y + 4} fontSize="9" fill="#334155">
                      {tick.value.toFixed(0)} GB
                    </text>
                  </g>
                ))}

                {monthlyChartV2.dayGrid.map((tick, idx) => (
                  <line key={`month-grid-${idx}`} x1={tick.x} y1={18} x2={tick.x} y2={204} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2 3" />
                ))}

                {monthlyChartV2.xTicks.map((tick, idx) => (
                  <text key={`month-x-${idx}`} x={tick.x - 14} y={226} fontSize="9" fill="#334155">
                    {tick.label}
                  </text>
                ))}

                <path d={monthlyChartV2.linePath} fill="none" stroke="#1d4ed8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                {monthlyChartV2.points.map((point, idx) => (
                  <g key={`month-p-${idx}`}>
                    <circle cx={point.x} cy={point.y} r={2.0} fill="#ffffff" stroke="#1e40af" strokeWidth="1.0" />
                    <title>{`${new Date(point.checkedAt).toLocaleString('ru-RU')} • ${point.freeGb.toFixed(2)} GB`}</title>
                  </g>
                ))}
              </svg>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('monitoringSettings')}
          className="w-full px-6 py-4 border-b flex items-center justify-between text-left hover:bg-gray-50"
        >
          <h2 className="text-xl font-semibold">Настройки мониторинга</h2>
          <span className="text-sm text-gray-500">{collapsedSections.monitoringSettings ? '▸' : '▾'}</span>
        </button>
        {!collapsedSections.monitoringSettings && (
        <div className="p-6 space-y-4">
          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Суточный PROD</h3>
            <div className="text-sm text-gray-600">Контролируемый путь: <span className="font-medium">{form.monitorPath}</span></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="text-sm">
                График PROD: минимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0} step="0.1" value={form.chartMinGb} onChange={(e) => setForm((p) => ({ ...p, chartMinGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: максимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.chartMaxGb} onChange={(e) => setForm((p) => ({ ...p, chartMaxGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: шаг линий (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.chartGridStepGb} onChange={(e) => setForm((p) => ({ ...p, chartGridStepGb: Number(e.target.value) }))} />
              </label>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Месячный PROD</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.monitoringEnabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, monitoringEnabled: e.target.checked }))}
                />
                Включить мониторинг
              </label>
              <div className="md:col-span-2" />
              <label className="text-sm">
                Интервал (мин)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.baseIntervalMinutes} onChange={(e) => setForm((p) => ({ ...p, baseIntervalMinutes: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Low-space порог (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} step="0.1" value={form.lowSpaceThresholdGb} onChange={(e) => setForm((p) => ({ ...p, lowSpaceThresholdGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Порог восстановления (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} step="0.1" value={form.recoveryThresholdGb} onChange={(e) => setForm((p) => ({ ...p, recoveryThresholdGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Напоминание (мин)
                <select className="mt-1 w-full border rounded px-3 py-2" value={form.reminderIntervalMinutes} onChange={(e) => setForm((p) => ({ ...p, reminderIntervalMinutes: Number(e.target.value) }))}>
                  {[1, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label className="text-sm">
                Retention (дней)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.retentionDays} onChange={(e) => setForm((p) => ({ ...p, retentionDays: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Stale multiplier
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.staleMultiplier} onChange={(e) => setForm((p) => ({ ...p, staleMultiplier: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: минимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0} step="0.1" value={form.monthlyChartMinGb} onChange={(e) => setForm((p) => ({ ...p, monthlyChartMinGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: максимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.monthlyChartMaxGb} onChange={(e) => setForm((p) => ({ ...p, monthlyChartMaxGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: шаг линий (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.monthlyChartGridStepGb} onChange={(e) => setForm((p) => ({ ...p, monthlyChartGridStepGb: Number(e.target.value) }))} />
              </label>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Месячный SFTP</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.sftpMonitoringEnabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, sftpMonitoringEnabled: e.target.checked }))}
                />
                Включить мониторинг SFTP
              </label>
              <div className="md:col-span-2 text-sm text-gray-600">
                Параметры подключения читаются из `SFTP_MONITOR_*` в `.env` PROD.
              </div>
              <label className="text-sm">
                Интервал SFTP (мин)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.sftpBaseIntervalMinutes} onChange={(e) => setForm((p) => ({ ...p, sftpBaseIntervalMinutes: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Stale multiplier SFTP
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.sftpStaleMultiplier} onChange={(e) => setForm((p) => ({ ...p, sftpStaleMultiplier: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Low-space SFTP (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.sftpLowSpaceThresholdGb} onChange={(e) => setForm((p) => ({ ...p, sftpLowSpaceThresholdGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Recovery SFTP (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.sftpRecoveryThresholdGb} onChange={(e) => setForm((p) => ({ ...p, sftpRecoveryThresholdGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                Напоминание SFTP (мин)
                <select className="mt-1 w-full border rounded px-3 py-2" value={form.sftpReminderIntervalMinutes} onChange={(e) => setForm((p) => ({ ...p, sftpReminderIntervalMinutes: Number(e.target.value) }))}>
                  {[1, 5, 10, 30, 60].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label className="text-sm">
                Retention SFTP (дней)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={1} value={form.sftpRetentionDays} onChange={(e) => setForm((p) => ({ ...p, sftpRetentionDays: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: минимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0} step="0.1" value={form.monthlySftpChartMinGb} onChange={(e) => setForm((p) => ({ ...p, monthlySftpChartMinGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: максимум (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.monthlySftpChartMaxGb} onChange={(e) => setForm((p) => ({ ...p, monthlySftpChartMaxGb: Number(e.target.value) }))} />
              </label>
              <label className="text-sm">
                График PROD: шаг линий (GB)
                <input className="mt-1 w-full border rounded px-3 py-2" type="number" min={0.1} step="0.1" value={form.monthlySftpChartGridStepGb} onChange={(e) => setForm((p) => ({ ...p, monthlySftpChartGridStepGb: Number(e.target.value) }))} />
              </label>
              <div className="md:col-span-3 text-xs text-gray-500">
                Статус SFTP: <span className="font-medium text-gray-700">{status?.sftpState?.schedulerStatus || '—'}</span>
                {' • '}Путь: <span className="font-medium text-gray-700">{status?.sftpState?.path || '—'}</span>
                {' • '}Последний успешный: <span className="font-medium text-gray-700">
                  {status?.sftpState?.lastSuccessfulCheckAt ? new Date(status.sftpState.lastSuccessfulCheckAt).toLocaleString('ru-RU') : '—'}
                </span>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Сводка (общая PROD + SFTP)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.dailySummaryEnabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, dailySummaryEnabled: e.target.checked }))}
                />
                Включить суточную сводку
              </label>
              <label className="text-sm">
                Время сводки (HH:mm)
                <input className="mt-1 w-full border rounded px-3 py-2" type="text" value={form.dailySummaryTime} onChange={(e) => setForm((p) => ({ ...p, dailySummaryTime: e.target.value }))} />
              </label>
              <label className="text-sm">
                Таймзона сводки
                <input className="mt-1 w-full border rounded px-3 py-2" type="text" value={form.dailySummaryTimezone} onChange={(e) => setForm((p) => ({ ...p, dailySummaryTimezone: e.target.value }))} />
              </label>
            </div>
          </div>
        </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('measurements')}
          className="w-full px-6 py-4 border-b flex items-center justify-between text-left hover:bg-gray-50"
        >
          <h2 className="text-lg font-semibold">Журнал замеров</h2>
          <span className="text-sm text-gray-500">{collapsedSections.measurements ? '▸' : '▾'}</span>
        </button>
        {!collapsedSections.measurements && (
        <>
        <div className="px-6 py-3 border-b flex items-center justify-end gap-3">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 flex items-center gap-2">
              Размер страницы
              <select
                className="w-20 border rounded px-2 py-1 text-sm"
                value={form.pageSizeMeasurements}
                onChange={(e) => handleMeasurementsPageSizeChange(Number(e.target.value))}
              >
                {[10, 20, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="text-sm text-gray-500">стр. {measurementsPage} / {measurementsTotalPages}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Время</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Free / Total</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Trigger</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Scheduler mode</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Ошибка</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {measurements.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2 text-sm">{new Date(m.checkedAt).toLocaleString('ru-RU')}</td>
                  <td className="px-4 py-2 text-sm">{m.status}</td>
                  <td className="px-4 py-2 text-sm">{m.freeGb ?? '—'} / {m.totalGb ?? '—'} GB</td>
                  <td className="px-4 py-2 text-sm">{m.trigger}</td>
                  <td className="px-4 py-2 text-sm">{m.schedulerMode}</td>
                  <td className="px-4 py-2 text-sm text-red-700">{m.errorMessage || '—'}</td>
                </tr>
              ))}
              {measurements.length === 0 && (
                <tr><td className="px-4 py-4 text-sm text-gray-500" colSpan={6}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <button disabled={measurementsPage <= 1} onClick={() => setMeasurementsPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Назад</button>
          <button disabled={measurementsPage >= measurementsTotalPages} onClick={() => setMeasurementsPage((p) => Math.min(measurementsTotalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Вперед</button>
        </div>
        </>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('notifications')}
          className="w-full px-6 py-4 border-b flex items-center justify-between text-left hover:bg-gray-50"
        >
          <h2 className="text-lg font-semibold">Журнал уведомлений</h2>
          <span className="text-sm text-gray-500">{collapsedSections.notifications ? '▸' : '▾'}</span>
        </button>
        {!collapsedSections.notifications && (
        <>
        <div className="px-6 py-3 border-b flex items-center justify-end gap-3">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 flex items-center gap-2">
              Размер страницы
              <select
                className="w-20 border rounded px-2 py-1 text-sm"
                value={form.pageSizeNotifications}
                onChange={(e) => handleNotificationsPageSizeChange(Number(e.target.value))}
              >
                {[10, 20, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="text-sm text-gray-500">стр. {notificationsPage} / {notificationsTotalPages}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Время</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Тип</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Ключ</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Попытки</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Причина/Ошибка</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {notifications.map((n) => (
                <tr key={n.id}>
                  <td className="px-4 py-2 text-sm">{new Date(n.createdAt).toLocaleString('ru-RU')}</td>
                  <td className="px-4 py-2 text-sm">{n.type}</td>
                  <td className="px-4 py-2 text-sm">{n.status}</td>
                  <td className="px-4 py-2 text-xs">{n.idempotencyKey}</td>
                  <td className="px-4 py-2 text-sm">{n.attemptCount}</td>
                  <td className="px-4 py-2 text-sm">{n.skipReason || n.error || '—'}</td>
                </tr>
              ))}
              {notifications.length === 0 && (
                <tr><td className="px-4 py-4 text-sm text-gray-500" colSpan={6}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <button disabled={notificationsPage <= 1} onClick={() => setNotificationsPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Назад</button>
          <button disabled={notificationsPage >= notificationsTotalPages} onClick={() => setNotificationsPage((p) => Math.min(notificationsTotalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Вперед</button>
        </div>
        </>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <button
          type="button"
          onClick={() => toggleSection('incidents')}
          className="w-full px-6 py-4 border-b flex items-center justify-between text-left hover:bg-gray-50"
        >
          <h2 className="text-lg font-semibold">Последние инциденты</h2>
          <span className="text-sm text-gray-500">{collapsedSections.incidents ? '▸' : '▾'}</span>
        </button>
        {!collapsedSections.incidents && (
        <>
        <div className="px-6 py-3 border-b flex items-center justify-end gap-3">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 flex items-center gap-2">
              Размер страницы
              <select
                className="w-20 border rounded px-2 py-1 text-sm"
                value={incidentsPageSize}
                onChange={(e) => {
                  setIncidentsPageSize(Number(e.target.value));
                  setIncidentsPage(1);
                }}
              >
                {[10, 20, 50, 100, 200].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="text-sm text-gray-500">стр. {incidentsPage} / {incidentsTotalPages}</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Старт</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Конец</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Статус</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500 uppercase">Min free</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidents.map((i) => (
                <tr key={i.id}>
                  <td className="px-4 py-2 text-sm">{new Date(i.startedAt).toLocaleString('ru-RU')}</td>
                  <td className="px-4 py-2 text-sm">{i.endedAt ? new Date(i.endedAt).toLocaleString('ru-RU') : '—'}</td>
                  <td className="px-4 py-2 text-sm">{i.status}</td>
                  <td className="px-4 py-2 text-sm">{i.minimumFreeGb} GB</td>
                </tr>
              ))}
              {incidents.length === 0 && (
                <tr><td className="px-4 py-4 text-sm text-gray-500" colSpan={4}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <button disabled={incidentsPage <= 1} onClick={() => setIncidentsPage((p) => Math.max(1, p - 1))} className="px-3 py-1 border rounded disabled:opacity-50">Назад</button>
          <button disabled={incidentsPage >= incidentsTotalPages} onClick={() => setIncidentsPage((p) => Math.min(incidentsTotalPages, p + 1))} className="px-3 py-1 border rounded disabled:opacity-50">Вперед</button>
        </div>
        </>
        )}
      </div>

    </div>
  );
};

export default ServerSettings;
